import clsx from 'clsx';
import { BiChevronRight } from 'react-icons/bi';

import { Card, Skeleton } from '@/components';
import { formatCurrency } from '@/utils/formatCurrency';

import s from './FinancialStatusCard.module.scss';

const SPARK_W = 58;
const SPARK_H = 16;

export type MonthlySpend = { label: string; amount: number };

const sparklinePoints = (series: number[], width: number, height: number) => {
  const pad = 1.5;
  const min = Math.min(...series);
  const max = Math.max(...series);
  const span = max - min || 1;
  const step = series.length > 1 ? (width - pad * 2) / (series.length - 1) : 0;

  return series
    .map((value, index) => {
      const x = pad + index * step;
      const y = height - pad - ((value - min) / span) * (height - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
};

const yearElapsedPct = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1).getTime();
  const end = new Date(now.getFullYear() + 1, 0, 1).getTime();
  return ((now.getTime() - start) / (end - start)) * 100;
};

const money = (amount: number) => `$${formatCurrency(amount)}`;

const compactMoney = (amount: number) =>
  `${amount < 0 ? '-' : ''}$${formatCurrency(Math.round(Math.abs(amount) / 1000))}k`;

type FinancialStatusCardProps = {
  year: string;
  totalPlanned: number;
  spent: number;
  monthlyYtd: MonthlySpend[];
  loading?: boolean;
  className?: string;
  to: string;
  compactValue?: boolean;
};

const FinancialStatusCard = ({
  year,
  totalPlanned,
  spent,
  monthlyYtd,
  loading,
  className,
  to,
  compactValue,
}: FinancialStatusCardProps) => {
  const remaining = totalPlanned - spent;
  const over = remaining < 0;
  const usedPct = totalPlanned > 0 ? (spent / totalPlanned) * 100 : 0;
  const remainingPct = 100 - usedPct;
  const barScale = Math.max(totalPlanned, spent);
  const fillPct = barScale > 0 ? (spent / barScale) * 100 : 0;
  const aheadOfPace = usedPct > yearElapsedPct();

  return (
    <Card
      to={to}
      ariaLabel={
        loading
          ? 'Financial status. View budget.'
          : `Financial status: ${money(remaining)} remaining of ${money(totalPlanned)} planned for ${year}. That's ${Math.round(remainingPct)}% of the budget, and ${aheadOfPace ? 'ahead of pace' : 'on track'} against the year. View budget.`
      }
      className={clsx(s.card, over && s.cardOver, className)}
    >
      <div className={s.topRow}>
        {loading ? (
          <Skeleton variant="block" width={220} height={44} />
        ) : (
          <p className={clsx(s.value, compactValue && s.valueCompact)}>
            {money(remaining)}
            <span className={s.valueUnit}>{over ? 'over budget' : 'remaining'}</span>
          </p>
        )}
        <span className={s.drill}>
          View budget <BiChevronRight aria-hidden="true" />
        </span>
      </div>

      {loading ? (
        <>
          <Skeleton variant="block" height={10} />
          <Skeleton variant="block" width={260} height={14} className={s.skeletonCaption} />
        </>
      ) : (
        <>
          <div
            className={s.track}
            role="progressbar"
            aria-valuenow={Math.round(fillPct)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className={clsx(s.fill, over && s.fillOver)}
              style={{ width: `${Math.min(100, fillPct)}%` }}
            />
          </div>

          <p className={s.caption}>
            <span className={s.captionStrong}>
              {compactMoney(spent)} spent of {compactMoney(totalPlanned)}
            </span>
            <span className={s.captionSep}>·</span>
            {over
              ? `${Math.round(usedPct - 100)}% over budget`
              : `${Math.round(remainingPct)}% of budget remaining`}
            <span className={s.captionSep}>·</span>
            <span className={clsx(s.verdict, aheadOfPace && s.verdictWarn)}>
              {aheadOfPace ? 'ahead of pace' : 'on track'}
            </span>

            {monthlyYtd.length > 1 && (
              <>
                <span className={s.captionSep}>·</span>
                <span className={s.sparkLabel}>YTD trend</span>
                <svg
                  className={s.spark}
                  width={SPARK_W}
                  height={SPARK_H}
                  viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
                  fill="none"
                  aria-hidden="true"
                >
                  <polyline
                    points={sparklinePoints(
                      monthlyYtd.map((month) => month.amount),
                      SPARK_W,
                      SPARK_H,
                    )}
                    stroke="currentColor"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className={s.srOnly}>
                  Monthly spend so far this year:{' '}
                  {monthlyYtd.map((month) => `${month.label} ${money(month.amount)}`).join(', ')}.
                </span>
              </>
            )}
          </p>
        </>
      )}
    </Card>
  );
};

export default FinancialStatusCard;
